import {
	add,
	endOfMonth,
	endOfYear,
	format,
	isAfter,
	startOfMonth,
	startOfYear,
	sub,
} from "date-fns";
import { useMemo, useState } from "react";
import {
	Bar,
	BarChart,
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from "recharts";

import { useConsumptionData } from "@/api/consumption";
import { useConsumption } from "@/context/ConsumptionContext";
import { useConfig } from "@/context/use-context";
import { CHART_COLORS } from "@/lib/constants";
import { formatValue } from "@/lib/format-for-view";
import YearlyDonutChart from "../charts/yearly-donut-chart";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

export interface ChartData {
	time: string;
	offPeak: number;
	peak: number;
	subscription?: number;
}

interface YearlyChartData {
	time?: string;
	offPeak?: number;
	peak?: number;
	subscription?: number;
	offPeakPrevious?: number;
	peakPrevious?: number;
	subscriptionPrevious?: number;
}

type ConsumBreakdownData = ChartData | YearlyChartData;

function CustomTooltip({
	active,
	payload,
	viewMode,
	totalValue,
	yearMode,
	year,
}: {
	active?: boolean;
	payload?: Array<{
		value?: number;
		name?: string;
		dataKey?: string;
		color?: string;
	}>;
	label?: string;
	viewMode: "kWh" | "Euro";
	totalValue?: number;
	yearMode?: boolean;
	year?: string;
}) {
	if (
		!active ||
		!payload ||
		payload.length === 0 ||
		payload[0] == null ||
		!totalValue
	) {
		return null;
	}

	let color = payload[0].color;
	if (!color) {
		if (yearMode) {
			if (year?.includes("2025")) {
				color =
					payload[0].name === "Heures Pleines"
						? CHART_COLORS.peak2025
						: CHART_COLORS.offPeak2025;
			} else {
				color =
					payload[0].name === "Heures Pleines"
						? CHART_COLORS.peak2024
						: CHART_COLORS.offPeak2024;
			}
		} else {
			color =
				payload[0].name === "Heures Pleines"
					? CHART_COLORS.peak
					: payload[0].name === "Heures Creuses"
						? CHART_COLORS.offPeak
						: CHART_COLORS.subscription;
		}
	}

	return (
		<div className="bg-white p-2 rounded shadow text-xs">
			<p className="font-semibold mb-1">{payload[0]?.name}</p>
			<div className="flex flex-col gap-1">
				{payload.map((entry) => (
					<div
						key={entry.name}
						className="flex items-center justify-between gap-2"
					>
						<div className="flex items-center">
							<div
								className="w-2 h-2 rounded-full mr-1"
								style={{ backgroundColor: entry.color }}
							/>
							<span>{entry.name}</span>
						</div>
						<span className="font-medium">
							{entry.name === "Abonnement"
								? formatValue(Number(entry.value), "Euro")
								: formatValue(Number(entry.value), viewMode)}
						</span>
					</div>
				))}
				<div className="mt-1 pt-1 border-t border-gray-100">
					<span className="font-medium">
						Total: {formatValue(Number(totalValue), viewMode)}
					</span>
				</div>
			</div>
		</div>
	);
}

// Common legend component
function ChartLegend({ viewMode }: { viewMode: "kWh" | "Euro" }) {
	return (
		<div className="flex flex-wrap gap-4 mt-4 justify-center">
			<div className="flex items-center">
				<div
					className="w-3 h-3 rounded-sm mr-2"
					style={{ backgroundColor: CHART_COLORS.offPeak }}
				/>
				<span className="text-xs text-gray-600">Heures Creuses</span>
			</div>
			<div className="flex items-center">
				<div
					className="w-3 h-3 rounded-sm mr-2"
					style={{ backgroundColor: CHART_COLORS.peak }}
				/>
				<span className="text-xs text-gray-600">Heures Pleines</span>
			</div>
			{viewMode === "Euro" && (
				<div className="flex items-center">
					<div
						className="w-3 h-3 rounded-sm mr-2"
						style={{ backgroundColor: CHART_COLORS.subscription }}
					/>
					<span className="text-xs text-gray-600">Abonnement</span>
				</div>
			)}
		</div>
	);
}

// Updated yearly chart legend component to use consistent colors
function YearlyChartLegend({ viewMode }: { viewMode: "kWh" | "Euro" }) {
	return (
		<div className="flex flex-col items-center gap-6 mt-4">
			<div className="flex gap-12">
				{/* 2024 Column */}
				<div className="flex flex-col gap-2">
					<div className="flex items-center">
						<div
							className="w-3 h-3 rounded-sm mr-2"
							style={{ backgroundColor: CHART_COLORS.offPeak2024 }}
						/>
						<span className="text-xs text-gray-600">Heures Creuses</span>
					</div>
					<div className="flex items-center">
						<div
							className="w-3 h-3 rounded-sm mr-2"
							style={{ backgroundColor: CHART_COLORS.peak2024 }}
						/>
						<span className="text-xs text-gray-600">Heures Pleines</span>
					</div>
				</div>

				{/* 2025 Column */}
				<div className="flex flex-col gap-2">
					<div className="flex items-center">
						<div
							className="w-3 h-3 rounded-sm mr-2"
							style={{ backgroundColor: CHART_COLORS.offPeak2025 }}
						/>
						<span className="text-xs text-gray-600">Heures Creuses</span>
					</div>
					<div className="flex items-center">
						<div
							className="w-3 h-3 rounded-sm mr-2"
							style={{ backgroundColor: CHART_COLORS.peak2025 }}
						/>
						<span className="text-xs text-gray-600">Heures Pleines</span>
					</div>
				</div>
			</div>

			{/* Only show subscription legend in Euro mode */}
			{viewMode === "Euro" && (
				<div className="flex items-center">
					<div
						className="w-3 h-3 rounded-sm mr-2"
						style={{ backgroundColor: CHART_COLORS.subscription }}
					/>
					<span className="text-xs text-gray-600">Abonnement</span>
				</div>
			)}
		</div>
	);
}

// Update DonutChart component to use consistent colors
function DonutChart({
	data,
	title,
	viewMode,
	showDate = false,
	date,
}: {
	data: ChartData[];
	title?: string;
	viewMode: "kWh" | "Euro";
	showDate?: boolean;
	date?: string;
}) {
	const { pod } = useConfig();
	const totalPeak = data.reduce((sum, item) => sum + item.peak, 0);
	const totalOffPeak = data.reduce((sum, item) => sum + item.offPeak, 0);
	const subscriptionValue = data.reduce(
		(sum, item) => sum + (item?.subscription || 0),
		0,
	);

	const total =
		totalPeak + totalOffPeak + (viewMode === "Euro" ? subscriptionValue : 0);

	const chartData =
		viewMode === "kWh"
			? [
					{
						name: "Heures Pleines",
						value: totalPeak,
						percentage: ((totalPeak / total) * 100).toFixed(1),
						color: CHART_COLORS.peak,
					},
					{
						name: "Heures Creuses",
						value: totalOffPeak,
						percentage: ((totalOffPeak / total) * 100).toFixed(1),
						color: CHART_COLORS.offPeak,
					},
				]
			: [
					{
						name: "Heures Pleines",
						value: totalPeak,
						percentage: ((totalPeak / total) * 100).toFixed(1),
						color: CHART_COLORS.peak,
					},
					{
						name: "Heures Creuses",
						value: totalOffPeak,
						percentage: ((totalOffPeak / total) * 100).toFixed(1),
						color: CHART_COLORS.offPeak,
					},
					{
						name: "Abonnement",
						value: subscriptionValue,
						percentage: ((subscriptionValue / total) * 100).toFixed(1),
						color: CHART_COLORS.subscription,
					},
				];

	return (
		<div className="flex flex-col items-center justify-center">
			{title && <p className="text-base font-medium mb-2">{title}</p>}

			<div className="relative">
				<PieChart width={220} height={220}>
					<Pie
						data={chartData}
						cx="50%"
						cy="50%"
						innerRadius={60}
						outerRadius={90}
						fill="#8884d8"
						dataKey="value"
						startAngle={90}
						endAngle={-270}
						labelLine={false}
					>
						{chartData.map((entry) => (
							<Cell
								key={`cell-${entry.name}`}
								fill={entry.color}
								style={{ outline: "none" }}
							/>
						))}
					</Pie>
					<Tooltip
						content={<CustomTooltip viewMode={viewMode} totalValue={total} />}
						isAnimationActive={false}
					/>
				</PieChart>
			</div>

			{showDate && <p className="text-xs text-gray-600 mt-2">{date}</p>}

			<ChartLegend viewMode={viewMode} />

			{/* Optional reminder of off-peak hours */}
			<div className="mt-4 text-xs">
				<p className="text-pink-500 font-medium">
					Rappel de mes heures creuses :{" "}
					{pod?.offpeak_hours
						.map(
							(hour) =>
								`${hour.start.split(":").join("h")} - ${hour.end.split(":").join("h")}`,
						)
						.join(", ")}
				</p>
			</div>
		</div>
	);
}

const granularityMap = {
	Daily: "HOURLY",
	Weekly: "DAILY",
	Monthly: "DAILY",
	Yearly: "MONTHLY",
} as const;

const timestampMap = {
	Daily: "HH:mm",
	Weekly: "dd/MM",
	Monthly: "dd/MM",
	Yearly: "dd/MM/yyyy",
};

export default function ConsumBreakdownChart() {
	const { unit, timeframe, selectedDate } = useConsumption();

	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const windowWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
	const { pod } = useConfig();
	const formatXAxis = (tick: string) => tick;
	const getBarSize = () => (windowWidth < 640 ? 10 : 16);
	const getBarGap = () => (windowWidth < 640 ? 2 : 4);

	// Calculate date ranges based on timeframe
	let startDate = selectedDate || sub(new Date(), { days: 2 });
	let endDate = add(startDate, { days: 1 });
	const yesterday = sub(new Date(), { days: 1 });

	let lastYearStartDate = startOfYear(sub(startDate, { years: 1 }));
	let lastYearEndDate = endOfYear(sub(endDate, { years: 1 }));

	const granularity = granularityMap[timeframe];

	if (timeframe === "Daily") {
		endDate = startDate;
	}

	if (timeframe === "Weekly") {
		const baseDate = selectedDate || sub(new Date(), { days: 1 });
		endDate = baseDate;
		startDate = sub(endDate, { days: 6 });
	}

	if (timeframe === "Monthly") {
		startDate = startOfMonth(startDate);
		endDate = endOfMonth(startDate);

		if (isAfter(endDate, yesterday)) {
			endDate = yesterday;
		}
	}

	if (timeframe === "Yearly") {
		const now = new Date();
		const currentYearStart = startOfYear(now);
		const currentDateMinus2 = sub(now, { days: 2 }); // Always 2 days behind

		startDate = currentYearStart;
		endDate = currentDateMinus2;

		lastYearStartDate = startOfYear(sub(now, { years: 1 }));
		lastYearEndDate = sub(currentDateMinus2, { years: 1 });
	}

	const {
		data: consumptionData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: granularity,
		start_date: format(startDate, "yyyy-MM-dd"),
		end_date: format(endDate, "yyyy-MM-dd"),
	});

	const lastYearData = useConsumptionData({
		energy: "ELEC",
		granularity: granularity,
		start_date: lastYearStartDate
			? format(lastYearStartDate, "yyyy-MM-dd")
			: undefined,
		end_date: lastYearEndDate
			? format(lastYearEndDate, "yyyy-MM-dd")
			: undefined,
		disabled: timeframe !== "Yearly",
	});

	// Format API data for charts
	const chartData = useMemo(() => {
		const result: ConsumBreakdownData[] = [];

		if (
			!consumptionData ||
			!consumptionData.data ||
			!Array.isArray(consumptionData.data)
		) {
			return [];
		}

		if (
			timeframe === "Yearly" &&
			lastYearData?.data &&
			Array.isArray(lastYearData.data.data)
		) {
			// For yearly view, format with current and previous year data
			const currentYearData = consumptionData.data;
			const previousYearData = lastYearData.data.data;

			let currentPeak = currentYearData.reduce(
				(sum, item) => sum + (item?.consumption_hp || 0),
				0,
			);
			let currentOffPeak = currentYearData.reduce(
				(sum, item) => sum + (item?.consumption_hc || 0),
				0,
			);
			let previousPeak = previousYearData.reduce(
				(sum, item) => sum + (item?.consumption_hp || 0),
				0,
			);
			let previousOffPeak = previousYearData.reduce(
				(sum, item) => sum + (item?.consumption_hc || 0),
				0,
			);

			if (unit === "Euro") {
				currentPeak = currentYearData.reduce(
					(sum, item) => sum + (item?.cost_hp || 0),
					0,
				);
				currentOffPeak = currentYearData.reduce(
					(sum, item) => sum + (item?.cost_hc || 0),
					0,
				);
				previousPeak = previousYearData.reduce(
					(sum, item) => sum + (item?.cost_hp || 0),
					0,
				);
				previousOffPeak = previousYearData.reduce(
					(sum, item) => sum + (item?.cost_hc || 0),
					0,
				);
			}
			// Format data for year-to-year comparison
			const currentYearSum = {
				time: format(startDate, "yyyy"),
				offPeak: currentOffPeak,
				peak: currentPeak,
				subscription: currentYearData.reduce(
					(sum, item) => sum + (item?.subscription_cost || 0),
					0,
				),
			};

			const previousYearSum = {
				time: format(sub(startDate, { years: 1 }), "yyyy"),
				offPeak: previousOffPeak,
				peak: previousPeak,
				subscription: previousYearData.reduce(
					(sum, item) => sum + (item?.subscription_cost || 100),
					0,
				),
			};

			result.push(previousYearSum, currentYearSum);
		} else {
			// For daily, weekly, monthly views
			for (const item of consumptionData.data) {
				let offPeak = item.consumption_hc || 0;
				let peak = item.consumption_hp || 0;

				if (unit === "Euro") {
					offPeak = item.cost_hc || 0;
					peak = item.cost_hp || 0;
				}
				result.push({
					time: format(new Date(item.timestamp), timestampMap[timeframe]),
					subscription: item.subscription_cost || 0,
					offPeak,
					peak,
				});
			}
		}

		return result;
	}, [consumptionData, timeframe, lastYearData.data, unit, startDate]);

	const getFormattedDate = () => {
		if (selectedDate) {
			return selectedDate.toLocaleDateString("fr-FR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});
		}
		return new Date().toLocaleDateString("fr-FR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	if (consumptionData?.data.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	// Error state
	if (error || !chartData || chartData.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	// Calculate yearly comparison text
	const calculateYearlyComparison = (data: ConsumBreakdownData[]) => {
		if (data.length < 2) return "";

		const currentYear = data.find((item) => item.time?.includes("2025"));
		const previousYear = data.find((item) => item.time?.includes("2024"));

		if (!currentYear || !previousYear) return "";

		const currentOffPeak = currentYear.offPeak || 0;
		const previousOffPeak = previousYear.offPeak || 0;

		if (previousOffPeak === 0) return "";

		const percentChange =
			((currentOffPeak - previousOffPeak) / previousOffPeak) * 100;
		const direction = percentChange > 0 ? "augmenté" : "diminué";

		return `Votre consommation d'heures creuses
                <br/>a ${direction} de ${Math.abs(percentChange).toFixed(
									1,
								)}% par rapport à l'année dernière`;
	};

	return (
		<Card className="w-full max-w-md md:max-w-full">
			<CardHeader className="flex flex-row items-start justify-between pb-2">
				<div>
					<CardTitle className="text-md font-semibold">
						{unit === "kWh"
							? "Répartition Heures Pleines et Heures Creuses"
							: "Répartition de vos dépenses"}
					</CardTitle>
					<p className="text-xs text-gray-500">
						{/* {selectedDate
                ? format(selectedDate, "dd/MM/yyyy")
                : "Sélectionnez une date"} */}
						{format(startDate, "dd/MM/yyyy")} - {format(endDate, "dd/MM/yyyy")}
					</p>
				</div>
			</CardHeader>

			<CardContent className="px-6">
				<div className="w-full">
					{timeframe === "Daily" ? (
						<div className="w-full flex justify-center py-4">
							<DonutChart
								data={chartData as ChartData[]}
								viewMode={unit}
								showDate={true}
								date={getFormattedDate()}
							/>
						</div>
					) : timeframe === "Yearly" ? (
						<div className="flex flex-col items-center">
							<div className="grid grid-cols-2 place-items-center gap-8 py-4">
								{chartData.map((data) => (
									<YearlyDonutChart
										key={data.time}
										data={data as ChartData}
										year={data.time || ""}
										viewMode={unit}
										startDate={startDate}
										endDate={endDate}
										lastYearStartDate={lastYearStartDate}
										lastYearEndDate={lastYearEndDate}
									/>
								))}
							</div>
							<YearlyChartLegend viewMode={unit} />
							{chartData.length >= 2 && (
								<p className="text-xs text-gray-600 mt-4">
									{calculateYearlyComparison(chartData)}
								</p>
							)}

							<p className="text-pink-500 text-xs mt-4 font-medium">
								Rappel de mes heures creuses :{" "}
								{pod?.offpeak_hours
									.map(
										(hour) =>
											`${hour.start.split(":").join("h")} - ${hour.end.split(":").join("h")}`,
									)
									.join(", ")}
							</p>
						</div>
					) : (
						<div className="w-full flex flex-col items-center">
							<div className="w-full h-[300px]">
								<ResponsiveContainer width="100%" height="100%">
									<BarChart
										width={windowWidth - 64}
										height={300}
										data={chartData}
										margin={{ top: 20, right: 0, left: 0, bottom: 30 }}
										barCategoryGap={getBarGap()}
										barGap={0}
										onMouseMove={(e) => {
											if (e && e.activeTooltipIndex !== undefined) {
												setActiveIndex(e.activeTooltipIndex);
											}
										}}
										onMouseLeave={() => setActiveIndex(null)}
									>
										<XAxis
											dataKey="time"
											tick={{ fontSize: windowWidth < 640 ? 10 : 12 }}
											tickFormatter={formatXAxis}
											axisLine={false}
											tickLine={false}
											height={30}
											padding={{ left: 5, right: 5 }}
										/>
										<Tooltip
											content={
												<CustomTooltip
													active={activeIndex !== null}
													label={chartData[activeIndex ?? 0]?.time}
													payload={[
														{
															value: chartData[activeIndex ?? 0]?.peak,
															name: "Heures Pleines",
															dataKey: "peak",
															color: CHART_COLORS.peak,
														},
														{
															value: chartData[activeIndex ?? 0]?.offPeak,
															name: "Heures Creuses",
															dataKey: "offPeak",
															color: CHART_COLORS.offPeak,
														},

														...(unit === "Euro"
															? [
																	{
																		value:
																			chartData[activeIndex ?? 0]?.subscription,
																		name: "Abonnement",
																		dataKey: "subscription",
																		color: CHART_COLORS.subscription,
																	},
																]
															: []),
													]}
													viewMode={unit}
													totalValue={
														(chartData[activeIndex ?? 0]?.peak || 0) +
														(chartData[activeIndex ?? 0]?.offPeak || 0) +
														(unit === "Euro"
															? chartData[activeIndex ?? 0]?.subscription || 0
															: 0)
													}
												/>
											}
											cursor={false}
											offset={20}
										/>
										{unit === "Euro" && (
											<Bar
												dataKey="subscription"
												name="Abonnement"
												stackId="a"
												fill={CHART_COLORS.subscription}
												barSize={getBarSize()}
											/>
										)}
										<Bar
											dataKey="offPeak"
											name="Heures Creuses"
											stackId="a"
											fill={CHART_COLORS.offPeak}
											barSize={getBarSize()}
										/>
										<Bar
											dataKey="peak"
											name="Heures Pleines"
											stackId="a"
											fill={CHART_COLORS.peak}
											barSize={getBarSize()}
											radius={[4, 4, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</div>

							{/* Added ChartLegend component for Weekly and Monthly views */}
							<ChartLegend viewMode={unit} />
							<p className="text-pink-500 text-xs mt-4 font-medium">
								Rappel de mes heures creuses :{" "}
								{pod?.offpeak_hours
									.map(
										(hour) =>
											`${hour.start.split(":").join("h")} - ${hour.end.split(":").join("h")}`,
									)
									.join(", ")}
							</p>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
